// AdminExcessRangeDeleteDialog.jsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  LinearProgress,
} from "@mui/material";

import api from "../../../api/axiosInterceptor";
import { LoadingSpinner } from "../../../Components/spinner/spinner";

export const AdminExcessRangeDeleteDialog = ({
  open,
  onClose,
  feName,
  feId,
  rows,
  onSuccess,
  onError,
  onDone,  // optional: to refetch after delete
}) => {
  const [deleting, setDeleting] = useState(false);
  const [startRefId, setStartRefId] = useState("");
  const [endRefId, setEndRefId] = useState("");
  const [startSlNo, setStartSlNo] = useState("");
  const [endSlNo, setEndSlNo] = useState("");
  const [selectedRangePreview, setSelectedRangePreview] = useState([]);

  // Range preview logic
  useEffect(() => {
    if (!rows.length) {
      setSelectedRangePreview([]);
      return;
    }
    const preview = [];
    if (startSlNo && endSlNo && !startRefId && !endRefId) {
      const s = parseInt(startSlNo) - 1;
      const e = parseInt(endSlNo) - 1;
      if (s >= 0 && e < rows.length && s <= e) {
        for (let i = s; i <= e; i++) preview.push(rows[i]);
      }
    } else if (startRefId && endRefId) {
      [...rows]
        .sort((a, b) => a.reference_id.localeCompare(b.reference_id))
        .forEach((r) => {
          if (r.reference_id >= startRefId && r.reference_id <= endRefId) {
            preview.push(r);
          }
        });
    }
    setSelectedRangePreview(preview);
  }, [startSlNo, endSlNo, startRefId, endRefId, rows]);

  // Reset helpers
  const resetRangeInputs = () => {
    setStartRefId("");
    setEndRefId("");
    setStartSlNo("");
    setEndSlNo("");
    setSelectedRangePreview([]);
  };

  const handleClose = () => {
    if (!deleting) {
      onClose();
      resetRangeInputs();
    }
  };

  const handleRangeDeleteSubmit = async () => {
    if (selectedRangePreview.length === 0) {
      onError("No records match the entered range.");
      return;
    }
    try {
      setDeleting(true);
      const referenceIds = selectedRangePreview.map((r) => r.reference_id);
      await api.delete("/excess_payment_mgmt/excess-master-bulk-delete/", {
        data: { reference_ids: referenceIds, fe_id: feId || undefined },
      });
      onSuccess(`Successfully deleted ${referenceIds.length} records.`);
      if (onDone) onDone();
    } catch (error) {
      onError(
        error?.response?.data?.error || "Failed to delete records."
      );
    } finally {
      setDeleting(false);
      onClose();
      resetRangeInputs();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Range Delete — by SL No OR Reference ID</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {/* Delete by SL No Range */}
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Delete by SL No Range
            </Typography>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                size="small"
                label="Start SL No"
                type="number"
                value={startSlNo}
                disabled={!!(startRefId || endRefId)}
                onChange={(e) => {
                  setStartSlNo(e.target.value);
                  if (e.target.value) {
                    setStartRefId("");
                    setEndRefId("");
                  }
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="End SL No"
                type="number"
                value={endSlNo}
                disabled={!!(startRefId || endRefId)}
                onChange={(e) => {
                  setEndSlNo(e.target.value);
                  if (e.target.value) {
                    setStartRefId("");
                    setEndRefId("");
                  }
                }}
              />
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            — OR —
          </Typography>

          {/* Delete by Reference ID Range */}
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Delete by Reference ID Range
            </Typography>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                size="small"
                label="Start Reference ID"
                value={startRefId}
                disabled={!!(startSlNo || endSlNo)}
                onChange={(e) => {
                  setStartRefId(e.target.value);
                  if (e.target.value) {
                    setStartSlNo("");
                    setEndSlNo("");
                  }
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="End Reference ID"
                value={endRefId}
                disabled={!!(startSlNo || endSlNo)}
                onChange={(e) => {
                  setEndRefId(e.target.value);
                  if (e.target.value) {
                    setStartSlNo("");
                    setEndSlNo("");
                  }
                }}
              />
            </Box>
          </Box>

          {/* Preview */}
          {selectedRangePreview.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                color="error"
                gutterBottom
              >
                Preview — {selectedRangePreview.length} record(s) will be deleted:
              </Typography>
              <Box
                sx={{
                  maxHeight: 180,
                  overflow: "auto",
                  bgcolor: "#fef2f2",
                  p: 2,
                  borderRadius: 1,
                  border: "1px solid #fca5a5",
                }}
              >
                {selectedRangePreview.slice(0, 8).map((row) => (
                  <Typography key={row.id} variant="body2" sx={{ mb: 0.5 }}>
                    • <strong>{row.reference_id}</strong> — {row.name}
                  </Typography>
                ))}
                {selectedRangePreview.length > 8 && (
                  <Typography variant="body2" color="text.secondary">
                    ... and {selectedRangePreview.length - 8} more
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {(startSlNo || endSlNo || startRefId || endRefId) &&
            selectedRangePreview.length === 0 && (
              <Typography variant="body2" color="warning.main">
                No records match the entered range.
              </Typography>
            )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          onClick={handleRangeDeleteSubmit}
          variant="contained"
          color="error"
          disabled={deleting || selectedRangePreview.length === 0}
        >
          {deleting
            ? "Deleting..."
            : `Delete ${selectedRangePreview.length} Record(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
